import { Component, computed, inject, OnInit, signal } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSortModule, Sort } from "@angular/material/sort";
import { Apollo, ApolloBase } from "apollo-angular";
import { Subscription as RxSubscription, interval } from "rxjs";
import { CERT_MITIGATION_PREFIXES_QUERY } from "../../../graphql/replication/cert-mitigation";
import { AuthService } from "../../../services/AuthService";
import { DialogAuthComponent } from "../../dialogs/dialog-auth/dialog-auth";
import { LoadingState } from "../../../services/loading-state";
import { IpPrefixDdosType, IpPrefixDdosTypeConnectionV2 } from "../../../gql/generated-ingestor";
import { openSelfServiceDialog } from "../../dialogs/dialog-selfservice/dialog-selfservice.helpers";
import { CommonModule } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { AngularSvgIconModule } from "angular-svg-icon";
import { LoaderComponent } from "../../common/loader/loader";
import { SelfserviceToggleComponent } from "../../selfservice/selfservice-toggle/selfservice-toggle";
import { IpVersionPipe } from "./ip-version-pipe";
import { MatTooltipModule } from "@angular/material/tooltip";

@Component({
  selector: "cert-mitigation",
  templateUrl: "cert-mitigation.html",
  styleUrls: ["cert-mitigation.scss"],
  imports: [
    CommonModule,
    IpVersionPipe,
    MatTooltipModule,
    LoaderComponent,
    MatSortModule,
    SelfserviceToggleComponent,
    TranslateModule,
    AngularSvgIconModule,
  ],
})
export class CertMitigationComponent implements OnInit {
  public auth = inject(AuthService);
  public loadingState = inject(LoadingState);
  public mitigationState: any = {};
  public loading = false;

  protected sortedData = computed(() => this.sortedSubscriptions());

  private dialog = inject(MatDialog);
  private apolloProvider = inject(Apollo);
  private subscriptions = signal<IpPrefixDdosType[]>([]);
  private sortedSubscriptions = computed(() => {
    if (!this.isSortActive()) {
      return this.subscriptions();
    }
    // waiting for Array.toSorted to appear in a stable node version (>23)
    // .sort changes the original array, while toSorted gives a sorted copy.
    // slice().sort() does the same thing as the latter.
    return this.subscriptions().slice().sort(this.sortFn);
  });
  private sort = signal<Sort>({
    active: "description",
    direction: "asc",
  });
  private isSortActive = computed(() => this.sort().active && this.sort().direction !== "");
  private replicatedData: ApolloBase;
  private refresherSubscription: RxSubscription | undefined = undefined;

  private sortFn = (a, b) => {
    const isAsc = this.sort().direction === "asc";
    switch (this.sort().active) {
      case "description":
        return this.compare(a.description, b.description, isAsc);
      case "prefix":
        // TODO: share this prefix sorting with the code in ip-prefixes
        let prefixA = a.prefix;
        let prefixB = b.prefix;
        if (prefixA === undefined) {
          prefixA = "";
        }
        if (prefixB === undefined) {
          prefixB = "";
        }
        // convert : and / to .
        prefixA = prefixA.replace(":", ".").replace("/", ".");
        prefixB = prefixB.replace(":", ".").replace("/", ".");

        prefixA = prefixA
          .split(".")
          .map((num) => num.padStart(3, "0"))
          .join("");
        prefixB = prefixB
          .split(".")
          .map((num) => num.padStart(3, "0"))
          .join("");

        // remove non numeric data
        prefixA = prefixA.replace(/\D/g, "");
        prefixB = prefixB.replace(/\D/g, "");

        return this.compare(Number(prefixA), Number(prefixB), isAsc);
      case "version":
        const versionA = a.afi;
        const versionB = b.afi;
        return this.compare(versionA, versionB, isAsc);

      default:
        return 0;
    }
  };

  constructor() {
    this.replicatedData = this.apolloProvider.use("replicatedData");
  }

  refreshSubscriptions() {
    this.replicatedData
      .query<{ ipPrefixDdos: IpPrefixDdosTypeConnectionV2 }>({
        query: CERT_MITIGATION_PREFIXES_QUERY,
        variables: { customer: localStorage.getItem("viewingCustomerGUID") },
        fetchPolicy: "network-only",
      })
      .subscribe(({ data }) => {
        this.subscriptions.set(data.ipPrefixDdos.page);
        this.loadingState.total = this.subscriptions().length;
        this.manageDirtinessRefresher();
      });
  }

  private manageDirtinessRefresher() {
    const dirtySubscriptionCount =
      this.subscriptions().filter((subscription) => subscription.dirty === true).length > 0;
    if (!dirtySubscriptionCount) {
      this.stopDirtinessRefresher();
    } else {
      this.startDirtinessRefresher();
    }
  }

  private startDirtinessRefresher() {
    if (!this.refresherSubscription) {
      const refresh = () => this.refreshSubscriptions();
      this.refresherSubscription = interval(5000).subscribe(refresh);
    }
  }

  private stopDirtinessRefresher() {
    if (this.refresherSubscription) {
      this.refresherSubscription.unsubscribe();
      this.refresherSubscription = undefined;
    }
  }

  ngOnInit() {
    this.refreshSubscriptions();
  }

  changeSort(sort: Sort): void {
    this.sort.set(sort);
  }

  compare(a: number | string, b: number | string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  toggleClick(subscription: IpPrefixDdosType) {
    // update automitigation
    if (!subscription.dirty) {
      this.openSelfserviceDialog(subscription);
    }
  }

  openSelfserviceDialog(subscription: IpPrefixDdosType) {
    const roleCheckResult = this.auth.checkRoleAccess("cert", "edit");
    if (!roleCheckResult.ok) {
      this.auth.roleEvent.emit(roleCheckResult);
      return;
    }

    if (!this.auth.selfServiceStarted()) {
      const data = { selfserviceState: { subscription }, initialAction: "autoMitigation" };
      this.dialog.open(DialogAuthComponent, { data });
    } else {
      const type = "autoMitigation";
      // TODO: typing gone terribly wrong. Adding useless typing that could be inferred, just because openSelfServiceDialog
      // accepts a vague union type is not a good idea. Probably a better idea to have specific self service dialogs
      const mitigationState: "enabled" | "disabled" = subscription.autoMitigation ? "enabled" : "disabled";
      const product: { type: "IpPrefixDdos"; tag: "" } = { type: "IpPrefixDdos", tag: "" };
      const subscriptionWithProduct = { ...subscription, product };

      const data = { type, subscription: subscriptionWithProduct, mitigationState };
      const dialogRef = openSelfServiceDialog(this.dialog, data);
      dialogRef.afterClosed().subscribe((event: string) => {
        if (event === "refresh") {
          // perform some optimistic rendering here
          // assume the toggle for mitigationState is successful.
          this.optimisticResult(subscription.subscriptionId, !subscription.autoMitigation);
          this.startDirtinessRefresher();
        }
      });
    }
  }

  optimisticResult(subscriptionId: string, mitigationState: boolean) {
    this.subscriptions.update((subscriptions) => {
      // replace the updated subscription with a new object
      // that has the new mitigation state.
      const index = subscriptions.findIndex((predicate) => predicate.subscriptionId === subscriptionId);
      const updatedSubscription = {
        ...subscriptions[index],
        dirty: true,
      };
      return [
        ...subscriptions.filter((subscription) => subscription.subscriptionId !== subscriptionId),
        updatedSubscription,
      ];
    });
  }
}
